import React, { useState, useEffect } from "react";
import api, { extractApiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, Plus, Pencil } from "lucide-react";
import { formatArabicDateTime } from "@/pages/admin/utils/format";
import AutoDiscountsPanel from "@/pages/admin/sections/AutoDiscountsPanel";



const CouponsAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {…} = edit
  const [form, setForm] = useState({
    code: "",
    kind: "percent",
    value: "",
    expires_at: "",
    active: true,
    scope: "all",
  });
  // Iteration 84 — admin-controlled visibility of the customer-facing
  // coupon input, independent per section. Purely a display switch: does
  // NOT touch coupon records, validation, Tap, pricing, or checkout.
  const [visibility, setVisibility] = useState({ sessions: true, book: true });
  const [visLoaded, setVisLoaded] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/coupons");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر تحميل الأكواد"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  // Iteration 84 — Load the persisted coupon-field visibility toggles.
  useEffect(() => {
    api
      .get("/admin/coupon-visibility")
      .then(({ data }) => {
        setVisibility({
          sessions: data.sessions !== false,
          book: data.book !== false,
        });
      })
      .catch((e) => toast.error(extractApiErrorMessage(e, "تعذّر تحميل إعدادات الظهور")))
      .finally(() => setVisLoaded(true));
  }, []);

  const saveVisibility = async (next) => {
    // Optimistic UI — revert on failure so the toggle never lies about
    // the persisted server state.
    const prev = visibility;
    setVisibility(next);
    try {
      const { data } = await api.put("/admin/coupon-visibility", next);
      setVisibility({
        sessions: data.sessions !== false,
        book: data.book !== false,
      });
      toast.success("تم حفظ إعدادات ظهور كود الخصم");
    } catch (e) {
      setVisibility(prev);
      toast.error(extractApiErrorMessage(e, "تعذّر حفظ الإعدادات"));
    }
  };

  const openNew = () => {
    setForm({ code: "", kind: "percent", value: "", expires_at: "", active: true, scope: "all" });
    setEditing({});
  };
  const openEdit = (c) => {
    setForm({
      code: c.code || "",
      kind: c.kind || "percent",
      value: c.value ?? "",
      expires_at: c.expires_at ? c.expires_at.slice(0, 16) : "",
      active: !!c.active,
      scope: c.scope || "all",
    });
    setEditing(c);
  };
  const closeForm = () => setEditing(null);

  const submit = async () => {
    if (!form.code.trim()) {
      toast.error("الكود مطلوب");
      return;
    }
    if (Number(form.value) <= 0) {
      toast.error("قيمة الخصم يجب أن تكون أكبر من صفر");
      return;
    }
    const body = {
      code: form.code.trim(),
      kind: form.kind,
      value: Number(form.value),
      active: !!form.active,
      scope: form.scope || "all",
      // datetime-local has no timezone, so let server treat as UTC ISO.
      expires_at: form.expires_at ? `${form.expires_at}:00Z` : null,
    };
    try {
      if (editing && editing.id) {
        await api.patch(`/admin/coupons/${editing.id}`, body);
        toast.success("تم تحديث الكود");
      } else {
        await api.post("/admin/coupons", body);
        toast.success("تم إنشاء الكود");
      }
      closeForm();
      refresh();
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر الحفظ"));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`حذف الكود ${c.code}؟ لا يمكن التراجع.`)) return;
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      toast.success("تم حذف الكود");
      refresh();
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر الحذف"));
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/admin/coupons/${c.id}`, { active: !c.active });
      refresh();
    } catch (e) {
      toast.error(extractApiErrorMessage(e, "تعذّر التحديث"));
    }
  };

  return (
    <div className="card-warm p-6" data-testid="admin-coupons-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-heading text-xl text-[#222222]">أكواد الخصم</h3>
          <p className="text-[#5E5C58] text-sm mt-1">
            إنشاء وتعديل وتفعيل أكواد خصم النسبة المئوية أو المبلغ الثابت.
            عند تطبيق كود يجعل الإجمالي صفرًا، يتم تخطّي بوابة الدفع تلقائيًا.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
          data-testid="admin-coupon-new-btn"
        >
          <Plus className="w-4 h-4 ms-1" /> كود جديد
        </Button>
      </div>

      {/* Iteration 84 — coupon-field visibility toggles (visual only) */}
      <div
        className="mt-5 rounded-2xl border border-[#E5E1D8] bg-[#FAF9F6] p-5"
        data-testid="admin-coupon-visibility-panel"
      >
        <h4 className="font-semibold text-[#222222] mb-1">إظهار خانة كود الخصم</h4>
        <p className="text-xs text-[#5E5C58] mb-4">
          يتحكم هذا الإعداد بعرض خانة إدخال الكود لدى العميلات فقط. لا يؤثر
          على الأكواد المسجّلة أو حساب الخصومات أو الدفع.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex items-center justify-between gap-3 rounded-xl border border-[#E5E1D8] bg-white px-4 py-3">
            <span className="text-sm text-[#222222]">
              إظهار خانة كود الخصم في الجلسات
            </span>
            <input
              type="checkbox"
              className="h-5 w-5 accent-[#0A4D3C]"
              data-testid="admin-coupon-visibility-sessions"
              disabled={!visLoaded}
              checked={visibility.sessions}
              onChange={(e) =>
                saveVisibility({ ...visibility, sessions: e.target.checked })
              }
            />
          </label>
          <label className="flex items-center justify-between gap-3 rounded-xl border border-[#E5E1D8] bg-white px-4 py-3">
            <span className="text-sm text-[#222222]">
              إظهار خانة كود الخصم في الكتاب
            </span>
            <input
              type="checkbox"
              className="h-5 w-5 accent-[#0A4D3C]"
              data-testid="admin-coupon-visibility-book"
              disabled={!visLoaded}
              checked={visibility.book}
              onChange={(e) =>
                saveVisibility({ ...visibility, book: e.target.checked })
              }
            />
          </label>
        </div>
      </div>

      {/* Iteration 90 — auto-discounts panel */}
      <AutoDiscountsPanel />

      {/* Inline edit form */}
      {editing && (
        <div
          className="mt-5 rounded-2xl border border-[#E5E1D8] bg-[#FAF9F6] p-5"
          data-testid="admin-coupon-form"
        >
          <h4 className="font-semibold text-[#222222] mb-3">
            {editing.id ? `تعديل ${editing.code}` : "إنشاء كود جديد"}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm text-[#5E5C58]">الكود</span>
              <input
                type="text"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="SAUDI94"
                dir="ltr"
                className="mt-1 w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2"
                data-testid="admin-coupon-code-input"
              />
            </label>
            <label className="block">
              <span className="text-sm text-[#5E5C58]">نوع الخصم</span>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                className="mt-1 w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2"
                data-testid="admin-coupon-kind-select"
              >
                <option value="percent">نسبة مئوية (%)</option>
                <option value="fixed">مبلغ ثابت (ر.س)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-sm text-[#5E5C58]">
                {form.kind === "percent" ? "النسبة (1–100)" : "المبلغ"}
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                className="mt-1 w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2"
                data-testid="admin-coupon-value-input"
              />
            </label>
            <label className="block">
              <span className="text-sm text-[#5E5C58]">
                تاريخ الانتهاء (اختياري)
              </span>
              <input
                type="datetime-local"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                className="mt-1 w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2"
                data-testid="admin-coupon-expires-input"
              />
            </label>
            <label className="block">
              <span className="text-sm text-[#5E5C58]">ينطبق على</span>
              <select
                value={form.scope}
                onChange={(e) => setForm({ ...form, scope: e.target.value })}
                className="mt-1 w-full rounded-xl border border-[#E5E1D8] bg-white px-3 py-2"
                data-testid="admin-coupon-scope-select"
              >
                <option value="all">الكل</option>
                <option value="book">الكتاب فقط</option>
                <option value="sessions">الجلسات فقط</option>
              </select>
            </label>
            <label className="flex items-center gap-2 mt-1">
              <input
                type="checkbox"
                checked={!!form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                data-testid="admin-coupon-active-checkbox"
                className="w-4 h-4 accent-[#0A4D3C]"
              />
              <span className="text-sm">مفعّل</span>
            </label>
          </div>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={submit}
              className="rounded-full bg-[#0A4D3C] hover:bg-[#063A2D] text-white"
              data-testid="admin-coupon-save-btn"
            >
              حفظ
            </Button>
            <Button
              onClick={closeForm}
              variant="outline"
              className="rounded-full"
              data-testid="admin-coupon-cancel-btn"
            >
              إلغاء
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="mt-6 overflow-x-auto" data-testid="admin-coupons-table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-[#F6F4EE] text-[#5E5C58]">
            <tr>
              <th className="text-start p-3">الكود</th>
              <th className="text-start p-3">النوع</th>
              <th className="text-start p-3">القيمة</th>
              <th className="text-start p-3">ينطبق على</th>
              <th className="text-start p-3">ينتهي</th>
              <th className="text-start p-3">الحالة</th>
              <th className="text-start p-3">الاستخدامات</th>
              <th className="text-start p-3">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-[#5E5C58]">
                  ...جاري التحميل
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={8} className="p-6 text-center text-[#5E5C58]">
                  لا توجد أكواد بعد. ابدأ بإنشاء واحد.
                </td>
              </tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-[#E5E1D8]" data-testid={`admin-coupon-row-${c.code}`}>
                <td className="p-3 font-mono" dir="ltr">{c.code}</td>
                <td className="p-3">
                  {c.kind === "percent" ? "نسبة %" : "مبلغ ثابت"}
                </td>
                <td className="p-3 font-semibold">
                  {c.kind === "percent" ? `${c.value}%` : `${c.value} ر.س`}
                </td>
                <td className="p-3" data-testid={`admin-coupon-scope-${c.code}`}>
                  {(() => {
                    const scope = c.scope || "all";
                    if (scope === "book") return "الكتاب فقط";
                    if (scope === "sessions") return "الجلسات فقط";
                    return "الكل";
                  })()}
                </td>
                <td className="p-3 text-[#5E5C58]">
                  {c.expires_at ? formatArabicDateTime(c.expires_at) : "—"}
                </td>
                <td className="p-3">
                  <Badge
                    className={
                      c.is_active_now
                        ? "rounded-full bg-emerald-100 text-emerald-700"
                        : "rounded-full bg-stone-200 text-stone-700"
                    }
                  >
                    {c.is_active_now ? "مفعّل" : "غير مفعّل"}
                  </Badge>
                </td>
                <td className="p-3 text-[#5E5C58]">{c.redemptions || 0}</td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => toggleActive(c)}
                      className="text-xs text-[#0A4D3C] underline"
                      data-testid={`admin-coupon-toggle-${c.code}`}
                    >
                      {c.active ? "تعطيل" : "تفعيل"}
                    </button>
                    <button
                      type="button"
                      onClick={() => openEdit(c)}
                      className="text-xs text-[#0A4D3C] underline"
                      data-testid={`admin-coupon-edit-${c.code}`}
                    >
                      <Pencil className="inline w-3 h-3" /> تعديل
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(c)}
                      className="text-xs text-rose-700 underline"
                      data-testid={`admin-coupon-delete-${c.code}`}
                    >
                      <Trash2 className="inline w-3 h-3" /> حذف
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export { CouponsAdmin };
